import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { addBidEvent } from "../../api/bid";
import { selectWorkgroupData } from "../../slices/commonConfig/commonConfigSlice";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import {
  IAM_CONTRACT,
  NC_PTO_FTO_BID_WORKGROUPS,
  NC_PTO_GOC_HOL_BID_WORKGROUPS,
  TWU_CONTRACT,
  WORKGROUP_CSA,
} from "../../util/constants";
import { BID_EVENT_TYPE, BID_SCHEDULED_STATUS, BID_STATUS, BID_TYPE } from "../../util/bid/bidConstants";
import { buildBidEvent } from "../../util/bid/bidEventBuilder";
import { validateBidType, validateWorkgroup } from "../../util/bid/bidInputValidation";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import {
  FREEDAY_OR_IAM_FTO_BID_MUST_PUBLISH_BEFORE_WARNING_MESSAGE,
  SHIFT_BID_MUST_PUBLISH_BEFORE_WARNING_MESSAGE,
} from "../../util/messageConstants";

const CreateBidDialog = (props) => {
  const userSession = useSelector(selectUserSession);
  const workgroupData = useSelector(selectWorkgroupData);
  // Form State
  const [bidType, setBidType] = useState("");
  const [bidTypeErrors, setBidTypeErrors] = useState("");
  const [workgroup, setWorkgroup] = useState("");
  const [workgroupErrors, setWorkgroupErrors] = useState("");

  // Submission State
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [saveErrors, setSaveErrors] = useState("");

  const handleBidTypeChange = (event) => {
    const bidTypeErrors = validateBidType(event.target.value);
    setBidTypeErrors(bidTypeErrors);

    setBidType(event.target.value);

    // select CSA workgroup for IAM FTO and IAM DAT bid types since it is one and only IAM workgroup
    if (event.target.value === BID_TYPE.FTO || event.target.value === BID_TYPE.IAM_DAT) {
      setWorkgroup(WORKGROUP_CSA);
    } else {
      //reset selected workgroup after bid type change
      setWorkgroup("");
    }
  };

  const handleWorkgroupChange = (event) => {
    const workgroupErrors = validateWorkgroup(event.target.value);
    setWorkgroupErrors(workgroupErrors);

    setWorkgroup(event.target.value);
  };

  const isFormValidationSuccess = () => {
    const bidTypeErrors = validateBidType(bidType);
    setBidTypeErrors(bidTypeErrors);
    const workgroupErrors = validateWorkgroup(workgroup);
    setWorkgroupErrors(workgroupErrors);

    return bidTypeErrors === "" && workgroupErrors === "";
  };

  const onSubmit = () => {
    if (!isFormValidationSuccess()) return;

    const payload = buildBidEvent(
      {
        bidType: bidType,
        eventType: BID_EVENT_TYPE.CREATE,
        scheduledStatus: BID_SCHEDULED_STATUS.NOT_SCHEDULED,
        station: props.station,
        status: BID_STATUS.PENDING,
        workgroup: workgroup,
      },
      undefined,
      userSession
    );

    setIsSaveLoading(true);
    setSaveErrors("");

    addBidEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error("Error");
          setSaveErrors(result.message);
        } else {
          if (result.pk) {
            // Expects pk to be in form BID_{referenceNumber}
            const newBidReferenceNumber = result.pk.split("_")[1];
            props.onClickDialogClose();
            props.onSubmitBidEvent(newBidReferenceNumber);
          }
        }
      })
      .catch((err) => {
        console.log("err", err);
        setSaveErrors("Unexpected Exception occurred.");
        setIsSaveLoading(false);
      });
  };

  const renderBidTypeMenuItems = () => {
    return Object.values(props.bidTypes)
      .slice()
      .sort()
      .map((bidType) => {
        return (
          <MenuItem key={bidType} value={bidType}>
            {bidType}
          </MenuItem>
        );
      });
  };

  const renderWorkgroupMenuItems = () => {
    let workGroupOptions = Object.keys(workgroupData);
    if (bidType === BID_TYPE.FREEDAY || bidType === BID_TYPE.TWU_DAT_FTO) {
      // show only TWU workgroups
      workGroupOptions = Object.keys(workgroupData).filter(
        (workGroup) => workgroupData[workGroup].unionContract === TWU_CONTRACT
      );
    } else if (bidType === BID_TYPE.FTO || bidType === BID_TYPE.IAM_DAT) {
      // show only IAM workgroups
      workGroupOptions = Object.keys(workgroupData).filter(
        (workGroup) => workgroupData[workGroup].unionContract === IAM_CONTRACT
      );
    } else if (bidType === BID_TYPE.NC_PTO_FTO) {
      // show only Noncontract PTO/FTO workgroups
      const NCPTOFTOWorkgroups = Object.values(NC_PTO_FTO_BID_WORKGROUPS);
      workGroupOptions = Object.keys(workgroupData).filter((workGroup) => NCPTOFTOWorkgroups.includes(workGroup));
    } else if (bidType === BID_TYPE.NC_PTO_GOC_HOL) {
      // show only Noncontract PTO/GOC-HOL workgroups
      const NCPTOGOCHOLWorkgroups = Object.values(NC_PTO_GOC_HOL_BID_WORKGROUPS);
      workGroupOptions = Object.keys(workgroupData).filter((workGroup) => NCPTOGOCHOLWorkgroups.includes(workGroup));
    }

    // For shift bid show all workgroups
    return workGroupOptions
      .slice()
      .sort()
      .map((workgroup) => {
        return (
          <MenuItem key={workgroup} value={workgroup}>
            {workgroup}
          </MenuItem>
        );
      });
  };

  const handleCreateClicked = () => {
    onSubmit();
  };

  const handleCancelClicked = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    props.onClickDialogClose();
  };

  return (
    <Dialog
      sx={{ width: "500px", margin: "auto" }}
      fullWidth
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={props.onClickDialogClose}
      data-testid="create-bid-dialog"
    >
      <Box p={1}>
        <DialogTitle id="create-bid-dialog-title">Create New Bid</DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"} id="alert-dialog-slide-description">
            <Stack direction="column" justifyContent="center">
              <Box m={1} pl={4} pr={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="bidTypeSelectLabel" error={bidTypeErrors !== ""}>
                    Bid Type
                  </InputLabel>
                  <Select
                    labelId="bidTypeSelectLabel"
                    id="bidTypeSelectElement"
                    value={bidType}
                    onChange={handleBidTypeChange}
                    label="Bid Type"
                    error={bidTypeErrors !== ""}
                  >
                    <MenuItem value="" disabled>
                      <em>Bid Type</em>
                    </MenuItem>
                    {renderBidTypeMenuItems()}
                  </Select>
                  {bidTypeErrors !== "" && <FormHelperText error>{bidTypeErrors}</FormHelperText>}
                </FormControl>
              </Box>
              <Box m={1} pl={4} pr={4}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="workgroupSelectLabel" error={workgroupErrors !== ""}>
                    Workgroup
                  </InputLabel>
                  <Select
                    labelId="workgroupSelectLabel"
                    id="workgroupSelectElement"
                    value={workgroup}
                    onChange={handleWorkgroupChange}
                    label="Workgroup"
                    error={workgroupErrors !== ""}
                    disabled={!bidType}
                  >
                    <MenuItem value="" disabled>
                      <em>Workgroup</em>
                    </MenuItem>
                    {renderWorkgroupMenuItems()}
                  </Select>
                  {workgroupErrors !== "" && <FormHelperText error>{workgroupErrors}</FormHelperText>}
                </FormControl>
              </Box>
              {(bidType === BID_TYPE.FREEDAY ||
                bidType === BID_TYPE.FTO ||
                bidType === BID_TYPE.NC_PTO_FTO ||
                bidType === BID_TYPE.NC_PTO_GOC_HOL) && (
                <Box m={1}>
                  <Alert severity="warning">{SHIFT_BID_MUST_PUBLISH_BEFORE_WARNING_MESSAGE}</Alert>
                </Box>
              )}
              {(bidType === BID_TYPE.IAM_DAT || bidType === BID_TYPE.TWU_DAT_FTO) && (
                <Box m={1}>
                  <Alert severity="warning">{FREEDAY_OR_IAM_FTO_BID_MUST_PUBLISH_BEFORE_WARNING_MESSAGE}</Alert>
                </Box>
              )}
              <Box m={1}>
                <Alert severity="info">
                  Clicking 'CREATE' will add a new bid in STARS. Please avoid creating duplicate bids.
                </Alert>
              </Box>
              {saveErrors !== "" && (
                <Box m={1}>
                  <Alert severity="error">
                    <AlertTitle>
                      <strong>SAVE ERROR:</strong>
                    </AlertTitle>
                    {saveErrors}
                  </Alert>
                </Box>
              )}
            </Stack>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id="cancelButton"
            sx={{ width: "100px" }}
            variant="outlined"
            color="primary"
            onClick={handleCancelClicked}
            disabled={isSaveLoading}
            size="large"
          >
            Cancel
          </Button>
          <Button
            id="saveButton"
            sx={{ width: "100px" }}
            variant="contained"
            color="primary"
            onClick={handleCreateClicked}
            disabled={isSaveLoading}
            size="large"
          >
            {isSaveLoading ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

CreateBidDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  station: PropTypes.string.isRequired,
  bidTypes: PropTypes.array.isRequired,
  onSubmitBidEvent: PropTypes.func.isRequired,
};

export default CreateBidDialog;
